import React, { useEffect, useMemo, useRef, useState } from "react";
import CollectionBuildSelect from "./CollectionBuildSelect";
import {
  ADD_SLOT,
  EMPTY_SLOT,
  MAX_QUANTITY,
  MAX_RANGE_PRODUCTS,
  RANGE_LABEL_COL,
  RANGE_PLAN_PAGE_SIZE,
  RANGE_PRODUCT_COL,
  RANGE_QTY_ROW_HEIGHT,
  RANGE_TYPE_ROW_HEIGHT,
  RangePlanItem,
  SelectOption,
  collectionBuildBorderClassName,
  collectionBuildDividerClassName,
  fieldLabelClassName,
  formFieldsShellClassName,
  getRangePlanPageCount,
  getRangePlanSlots,
  helperTextClassName,
  padRangePlanPage,
  RequiredMark,
  scrollCollectionSectionIntoView,
  sectionBlockClassName,
  sectionHelperClassName,
  sectionTitleClassName,
  sliceRangePlanPage,
  sumRangePlanQuantity,
} from "./collectionBuildConstants";

type Props = {
  items: RangePlanItem[];
  productOptions: SelectOption[];
  canAdd: boolean;
  onChange: (id: string, patch: Partial<RangePlanItem>) => void;
  onAdd: () => void;
  onRemove: (id: string) => void;
};

const QuantityStepper: React.FC<{
  value: number;
  maxValue: number;
  onChange: (value: number) => void;
}> = ({ value, maxValue, onChange }) => (
  <div className="mx-auto flex h-8 w-[95px] min-w-0 items-center rounded-full bg-[#D3C0B626]">
    <button
      type="button"
      aria-label="Decrease quantity"
      disabled={value <= 0}
      onClick={() => onChange(Math.max(0, value - 1))}
      className="flex h-8 w-8 shrink-0 items-center justify-center text-base text-secondary focus:outline-none disabled:cursor-not-allowed disabled:opacity-40"
    >
      −
    </button>
    <span className="min-w-0 flex-1 text-center text-[15px] leading-[15px] text-secondary">
      {String(value).padStart(2, "0")}
    </span>
    <button
      type="button"
      aria-label="Increase quantity"
      disabled={value >= maxValue}
      onClick={() => onChange(Math.min(maxValue, value + 1))}
      className="flex h-8 w-8 shrink-0 items-center justify-center text-base text-secondary focus:outline-none disabled:cursor-not-allowed disabled:opacity-40"
    >
      +
    </button>
  </div>
);

const PageArrow: React.FC<{
  direction: "prev" | "next";
  disabled: boolean;
  onClick: () => void;
}> = ({ direction, disabled, onClick }) => (
  <button
    type="button"
    aria-label={direction === "prev" ? "Previous products" : "Next products"}
    disabled={disabled}
    onClick={onClick}
    className="flex h-8 w-8 items-center justify-center rounded-full text-lg leading-none text-secondary focus:outline-none disabled:cursor-not-allowed disabled:opacity-30"
  >
    {direction === "prev" ? "‹" : "›"}
  </button>
);

const CollectionBuildRangePlan: React.FC<Props> = ({
  items,
  productOptions,
  canAdd,
  onChange,
  onAdd,
  onRemove,
}) => {
  const [page, setPage] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);
  const totalQuantity = sumRangePlanQuantity(items);
  const remainingUnits = Math.max(0, MAX_RANGE_PRODUCTS - totalQuantity);
  const slots = useMemo(
    () => getRangePlanSlots(items, canAdd),
    [items, canAdd]
  );
  const pageCount = getRangePlanPageCount(slots.length);
  const paginated = slots.length > RANGE_PLAN_PAGE_SIZE;
  const visible = padRangePlanPage(sliceRangePlanPage(slots, page), paginated);
  const columnCount = paginated ? RANGE_PLAN_PAGE_SIZE : visible.length;

  useEffect(() => {
    setPage(current => Math.min(current, Math.max(0, pageCount - 1)));
  }, [pageCount]);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      scrollCollectionSectionIntoView(sectionRef.current, "nearest");
    });
    return () => window.cancelAnimationFrame(frame);
  }, []);

  return (
    <div ref={sectionRef} className={sectionBlockClassName}>
      <div className={formFieldsShellClassName}>
        <div className="flex items-center gap-3">
          <h2 className={sectionTitleClassName}>Range Plan</h2>
          <span className="shrink-0 rounded-md bg-[#D3C0B626] px-2 py-0.5 text-sm font-medium tabular-nums text-secondary">
            {totalQuantity}/{MAX_RANGE_PRODUCTS}
          </span>
        </div>
        <p className={sectionHelperClassName}>
          Add the product types and quantity you want in this collection.
        </p>
        <div className="min-w-0 w-fit max-w-full overflow-x-auto overflow-y-visible">
          <div
            className={`grid w-fit min-w-0 overflow-visible rounded-lg border ${collectionBuildBorderClassName}`}
            style={{
              gridTemplateColumns: `${RANGE_LABEL_COL} repeat(${Math.max(columnCount, 1)}, ${RANGE_PRODUCT_COL})`,
              gridTemplateRows: `${RANGE_TYPE_ROW_HEIGHT} ${RANGE_QTY_ROW_HEIGHT}`,
            }}
          >
            <div
              className={`${fieldLabelClassName} mb-0 flex h-full w-full items-center justify-center whitespace-nowrap rounded-tl-lg border-b border-r text-center ${collectionBuildDividerClassName} px-4 py-3`}
              style={{ gridColumn: 1, gridRow: 1 }}
            >
              Product Type
              <RequiredMark />
            </div>
            <div
              className={`${fieldLabelClassName} mb-0 flex h-full items-center whitespace-nowrap rounded-bl-lg border-r ${collectionBuildDividerClassName} px-4 py-3`}
              style={{ gridColumn: 1, gridRow: 2 }}
            >
              Quantity
              <RequiredMark />
            </div>
            {visible.map((slot, slotIndex) => {
              const column = slotIndex + 2;
              const isLast = slotIndex === visible.length - 1;
              const verticalRule = isLast
                ? ""
                : `border-r ${collectionBuildDividerClassName}`;

              if (slot === ADD_SLOT) {
                return (
                  <button
                    key="add-product"
                    type="button"
                    onClick={onAdd}
                    style={{ gridColumn: column, gridRow: "1 / span 2" }}
                    className={`flex h-full items-center justify-center gap-1.5 px-2 text-center text-xs font-medium leading-tight text-tertiary hover:bg-lightGray focus:outline-none ${verticalRule} ${
                      isLast ? "rounded-r-lg" : ""
                    }`}
                  >
                    <span aria-hidden className="text-sm leading-none">
                      +
                    </span>
                    Add Another product
                  </button>
                );
              }

              if (slot === EMPTY_SLOT) {
                return (
                  <React.Fragment key={`empty-${slotIndex}`}>
                    <div
                      className={`h-full border-b ${collectionBuildDividerClassName} ${verticalRule} ${
                        isLast ? "rounded-tr-lg" : ""
                      }`}
                      style={{ gridColumn: column, gridRow: 1 }}
                    />
                    <div
                      className={`h-full ${verticalRule} ${
                        isLast ? "rounded-br-lg" : ""
                      }`}
                      style={{ gridColumn: column, gridRow: 2 }}
                    />
                  </React.Fragment>
                );
              }

              const selectedType = productOptions.some(
                option => option.value === slot.productType
              )
                ? slot.productType
                : "";

              return (
                <React.Fragment key={slot.id}>
                  <div
                    className={`relative z-10 flex h-full items-center overflow-visible border-b ${collectionBuildDividerClassName} px-3 py-3 ${verticalRule} ${
                      isLast ? "rounded-tr-lg" : ""
                    }`}
                    style={{ gridColumn: column, gridRow: 1 }}
                  >
                    <button
                      type="button"
                      aria-label="Remove product"
                      onClick={() => onRemove(slot.id)}
                      className="absolute right-0.5 top-0 z-[1] flex h-3.5 w-3.5 items-center justify-center text-[11px] leading-none text-tertiary hover:text-secondary focus:outline-none"
                    >
                      ×
                    </button>
                    <CollectionBuildSelect
                      value={selectedType}
                      placeholder="Select product"
                      options={productOptions}
                      onChange={productType =>
                        onChange(slot.id, { productType })
                      }
                      compact
                    />
                  </div>
                  <div
                    className={`flex h-full items-center px-3 py-3 ${verticalRule} ${
                      isLast ? "rounded-br-lg" : ""
                    }`}
                    style={{ gridColumn: column, gridRow: 2 }}
                  >
                    <QuantityStepper
                      value={slot.quantity}
                      maxValue={Math.min(
                        MAX_QUANTITY,
                        slot.quantity + remainingUnits
                      )}
                      onChange={quantity => onChange(slot.id, { quantity })}
                    />
                  </div>
                </React.Fragment>
              );
            })}
          </div>
          {paginated && (
            <div className="mt-3 flex items-center justify-center gap-2">
              <PageArrow
                direction="prev"
                disabled={page <= 0}
                onClick={() => setPage(current => Math.max(0, current - 1))}
              />
              <PageArrow
                direction="next"
                disabled={page >= pageCount - 1}
                onClick={() =>
                  setPage(current => Math.min(pageCount - 1, current + 1))
                }
              />
            </div>
          )}
        </div>
        <p className={`${helperTextClassName} mt-3`}>
          *Maximum {MAX_RANGE_PRODUCTS} products you can add.
        </p>
      </div>
    </div>
  );
};

export default CollectionBuildRangePlan;
